import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { RELIABILITY, normRel } from "@/lib/events";

const FRANCE_CENTER = [46.6, 2.5];

function pinSvg(rel, termine) {
  const color = termine ? "#64748B" : (RELIABILITY[rel] || RELIABILITY.annonce).color;
  const opacity = !termine && rel === "reseaux_sociaux" ? 0.6 : 1;
  let stroke = "#0B0F17", strokeW = 1.5, dash = "";
  if (!termine && rel === "confirme") { stroke = "#F59E0B"; strokeW = 3; }
  else if (!termine && rel === "annonce") { stroke = "#7C2D12"; strokeW = 2; dash = 'stroke-dasharray="3 2"'; }
  else if (!termine && rel === "reseaux_sociaux") { stroke = "#6D5B8E"; strokeW = 1.5; }
  let glyph = "";
  if (!termine && rel === "confirme") glyph = '<path d="M12.5 14 l2.2 2.2 l4.2 -4.4" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
  else if (!termine && rel === "annonce") glyph = '<text x="16" y="17.5" text-anchor="middle" font-size="9" font-weight="800" fill="#fff">?</text>';
  else if (!termine && rel === "reseaux_sociaux") glyph = '<path d="M11 10.5 h10 a1.6 1.6 0 0 1 1.6 1.6 v3.4 a1.6 1.6 0 0 1 -1.6 1.6 h-4.5 l-3 2 v-2 h-2.5 a1.6 1.6 0 0 1 -1.6 -1.6 v-3.4 a1.6 1.6 0 0 1 1.6 -1.6 z" fill="#fff"/>';
  return `
  <div style="position:relative;transform:translate(-50%,-100%)">
    <svg width="34" height="44" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 1C8.8 1 3 6.8 3 14c0 9.2 13 27 13 27s13-17.8 13-27C29 6.8 23.2 1 16 1z" fill="${color}" fill-opacity="${opacity}" stroke="${stroke}" stroke-width="${strokeW}" ${dash}/>
      <circle cx="16" cy="14" r="6" fill="#0B0F17"/>
      ${glyph}
    </svg>
  </div>`;
}

export default function MapView({ events, userLoc, radius, onSelect, focusId, active }) {
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const circleRef = useRef(null);
  const avoidRef = useRef(null);
  const markersById = useRef({});

  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map("am-map", { zoomControl: false, attributionControl: true }).setView(FRANCE_CENTER, 6);
    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        "&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors",
      maxZoom: 19,
      subdomains: "abc",
    }).addTo(map);
    const cluster = L.markerClusterGroup({ maxClusterRadius: 50, disableClusteringAtZoom: 12 });
    map.addLayer(cluster);
    const avoid = L.layerGroup().addTo(map);
    mapRef.current = map;
    clusterRef.current = cluster;
    avoidRef.current = avoid;
    return () => {
      try { map.remove(); } catch (e) {}
      mapRef.current = null;
      clusterRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (map && active) {
      setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 150);
    }
  }, [active]);

  useEffect(() => {
    const cluster = clusterRef.current;
    const avoid = avoidRef.current;
    if (!cluster) return;
    cluster.clearLayers();
    avoid.clearLayers();
    markersById.current = {};
    (events || []).forEach((ev) => {
      const icon = L.divIcon({
        className: "am-pin",
        html: pinSvg(normRel(ev.reliability), ev.status === "termine"),
        iconSize: [34, 44],
        iconAnchor: [17, 44],
      });
      const m = L.marker([ev.lat, ev.lng], { icon });
      m.on("click", () => onSelect(ev));
      cluster.addLayer(m);
      markersById.current[ev.id] = m;
      if (ev.type === "blocage_routier" && ev.status !== "termine") {
        L.circle([ev.lat, ev.lng], { radius: 400, color: "#DC2626", weight: 1, fillColor: "#DC2626", fillOpacity: 0.12 }).addTo(avoid);
      }
    });
  }, [events, onSelect]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !userLoc) return;
    if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; }
    const grp = L.layerGroup([
      L.circle([userLoc.lat, userLoc.lng], {
        radius: (radius || 25) * 1000,
        color: "#38BDF8",
        weight: 1.5,
        fillColor: "#38BDF8",
        fillOpacity: 0.06,
      }),
      L.circleMarker([userLoc.lat, userLoc.lng], { radius: 6, color: "#38BDF8", fillColor: "#38BDF8", fillOpacity: 1 }),
    ]).addTo(map);
    circleRef.current = grp;
    map.setView([userLoc.lat, userLoc.lng], radius <= 10 ? 11 : radius <= 25 ? 10 : 9);
  }, [userLoc, radius]);

  useEffect(() => {
    const map = mapRef.current;
    const m = markersById.current[focusId];
    if (map && m && focusId) {
      map.setView(m.getLatLng(), 13, { animate: true });
    }
  }, [focusId]);

  return <div id="am-map" data-testid="map-container" className="absolute inset-0 h-full w-full" />;
}
